import React from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import CollapseCard from "components/Collapse";
import TwoColumnContainer from "components/Container/TwoColumnContainer";
import { formatTimestamp, formatDuration } from "utils/format";
import {
  MOCK_NVR_HARD_DRIVE_INFO,
  MOCK_NVR_INTERNET_INFO,
  MOCK_NVR_SYSTEM_INFO,
} from "./const";

const StyledTypography = styled((props) => (
  <Typography component="div" {...props} />
))(({ theme }) => ({
  fontSize: theme.typography.customFontSize[2],
  color: theme.palette.primary.black,
  fontWeight: theme.typography.customFontWeight[1],
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: theme.typography.customFontSize[2],
  color: theme.palette.primary.black,
  whiteSpace: "nowrap",
  paddingTop: theme.spacing(0.75),
  paddingBottom: theme.spacing(0.75),
}));

const InfoTable = ({ columns, rows }) => (
  <TableContainer sx={{ overflowX: "auto" }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          {columns.map((column) => (
            <StyledTableCell
              key={column.key}
              sx={{ fontWeight: "bold", backgroundColor: "#F5F5F5" }}
            >
              {column.label}
            </StyledTableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, index) => (
          <TableRow key={row.id ?? index}>
            {columns.map((column) => (
              <StyledTableCell key={column.key}>
                {row[column.key] ?? "-"}
              </StyledTableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const SurveillancePanel = ({
  infoData,
  networkData,
  timeZone,
  batchLoading,
}) => {
  const isNvr = infoData.deviceType === "nvr";

  const getUptimeValue = () => {
    const status = infoData.linkStatus;
    const uptime = infoData.uptime;
    const lastSeen = infoData.lastOfflineTime;

    if (status === "offline") {
      return lastSeen !== null ? formatTimestamp(lastSeen) : "-";
    } else if (status === "online") {
      return uptime !== null
        ? formatDuration(Math.max(0, Math.floor(Date.now() / 1000) - uptime))
        : "-";
    }
    return "-";
  };

  const disks = infoData.storageInfo?.disks ?? [];
  const freeSpace = infoData.storageInfo?.freeSpace;
  const alarmIn = infoData.alarmIoInfo?.alarmIn ?? [];
  const alarmOut = infoData.alarmIoInfo?.alarmOut ?? [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", width: "100%" }}>
      {/* Device Panel */}
      <CollapseCard title="Device">
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <TwoColumnContainer label="Site">
            <StyledTypography>{infoData.siteName ?? "-"}</StyledTypography>
          </TwoColumnContainer>
          <TwoColumnContainer label="Device Type">
            <StyledTypography>{isNvr ? "NVR" : "IPC"}</StyledTypography>
          </TwoColumnContainer>
          {isNvr && (
            <TwoColumnContainer label="Max Channels">
              <StyledTypography>{infoData.maxChannels ?? 8}</StyledTypography>
            </TwoColumnContainer>
          )}
          <TwoColumnContainer label="Model">
            <StyledTypography>{infoData.modelName ?? "-"}</StyledTypography>
          </TwoColumnContainer>
          <TwoColumnContainer label="Firmware Version">
            <StyledTypography>{infoData.firmwareVersion ?? "-"}</StyledTypography>
          </TwoColumnContainer>
          <TwoColumnContainer label="SN">
            <StyledTypography>{infoData.serialNumber ?? "-"}</StyledTypography>
          </TwoColumnContainer>
          <TwoColumnContainer label="IP Address">
            <StyledTypography>{infoData.localIpAddr ?? networkData?.localIp ?? "-"}</StyledTypography>
          </TwoColumnContainer>
          <TwoColumnContainer label="MAC Address">
            <StyledTypography>{infoData.macAddress ?? "-"}</StyledTypography>
          </TwoColumnContainer>
          <TwoColumnContainer label="Uptime">
            <StyledTypography>{getUptimeValue()}</StyledTypography>
          </TwoColumnContainer>
          <TwoColumnContainer label="Time Zone">
            <StyledTypography>
              {batchLoading ? <CircularProgress size={16} /> : (timeZone || infoData.timeZone || "-")}
            </StyledTypography>
          </TwoColumnContainer>
          <TwoColumnContainer label="Joined On">
            <StyledTypography>
              {infoData?.joinedOn ? formatTimestamp(infoData.joinedOn) : "-"}
            </StyledTypography>
          </TwoColumnContainer>
        </Box>
      </CollapseCard>

      {/* Storage Info Panel (NVR Only) */}
      {isNvr && (
        <CollapseCard title="Storage Info">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <TwoColumnContainer label="Disk Status">
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {disks.length > 0 ? disks.map((disk, idx) => (
                  <StyledTypography key={idx}>
                    {disk.name}: {disk.status}
                  </StyledTypography>
                )) : <StyledTypography>-</StyledTypography>}
              </Box>
            </TwoColumnContainer>
            <TwoColumnContainer label="Disk Free Space">
              <StyledTypography>{freeSpace ?? "-"}</StyledTypography>
            </TwoColumnContainer>
          </Box>
        </CollapseCard>
      )}

      {/* System Info Panel (NVR Only) — mock until API */}
      {isNvr && (
        <CollapseCard title="System Info">
          <InfoTable
            columns={[
              { key: "channel", label: "Channel" },
              { key: "name", label: "Name" },
              { key: "status", label: "Status" },
              { key: "videoFormat", label: "Video Format" },
              { key: "resolution", label: "Resolution" },
              { key: "bitrate", label: "Bitrate" },
            ]}
            rows={MOCK_NVR_SYSTEM_INFO}
          />
        </CollapseCard>
      )}

      {/* Hard Drive Info Panel (NVR Only) — mock until API */}
      {isNvr && (
        <CollapseCard title="Hard Drive Info">
          <InfoTable
            columns={[
              { key: "disk", label: "Disk" },
              { key: "capacity", label: "Capacity" },
              { key: "used", label: "Used" },
              { key: "sn", label: "SN" },
              { key: "diskModel", label: "Disk Model" },
              { key: "status", label: "Status" },
            ]}
            rows={MOCK_NVR_HARD_DRIVE_INFO}
          />
        </CollapseCard>
      )}

      {/* Internet Info Panel (NVR Only) — mock until API */}
      {isNvr && (
        <CollapseCard title="Internet Info">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <TwoColumnContainer label="Status">
              <StyledTypography>{MOCK_NVR_INTERNET_INFO.status}</StyledTypography>
            </TwoColumnContainer>
            <TwoColumnContainer label="IP">
              <StyledTypography>{MOCK_NVR_INTERNET_INFO.ip}</StyledTypography>
            </TwoColumnContainer>
            <TwoColumnContainer label="IPv4 CCTV">
              <StyledTypography>{MOCK_NVR_INTERNET_INFO.ipv4Cctv}</StyledTypography>
            </TwoColumnContainer>
            <TwoColumnContainer label="Subnet Mask">
              <StyledTypography>{MOCK_NVR_INTERNET_INFO.subnetMask}</StyledTypography>
            </TwoColumnContainer>
            <TwoColumnContainer label="Default Gateway">
              <StyledTypography>{MOCK_NVR_INTERNET_INFO.defaultGateway}</StyledTypography>
            </TwoColumnContainer>
            <TwoColumnContainer label="MAC Address">
              <StyledTypography>{MOCK_NVR_INTERNET_INFO.macAddress}</StyledTypography>
            </TwoColumnContainer>
            <TwoColumnContainer label="DHCP">
              <StyledTypography>{MOCK_NVR_INTERNET_INFO.dhcp}</StyledTypography>
            </TwoColumnContainer>
            <TwoColumnContainer label="Preferred DNS Server">
              <StyledTypography>{MOCK_NVR_INTERNET_INFO.preferredDns}</StyledTypography>
            </TwoColumnContainer>
            <TwoColumnContainer label="Alternative DNS Server">
              <StyledTypography>{MOCK_NVR_INTERNET_INFO.alternativeDns}</StyledTypography>
            </TwoColumnContainer>
            <TwoColumnContainer label="Total Bandwidth">
              <StyledTypography>{MOCK_NVR_INTERNET_INFO.totalBandwidth}</StyledTypography>
            </TwoColumnContainer>
          </Box>
        </CollapseCard>
      )}

      {/* Alarm I/O Info Panel (NVR Only) */}
      {isNvr && (
        <CollapseCard title="Alarm I/O Info">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <TwoColumnContainer label="Alarm in Status">
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {alarmIn.length > 0 ? alarmIn.map((alarm) => (
                  <StyledTypography key={alarm.id}>
                    Alarm in {alarm.id}: {alarm.status}
                  </StyledTypography>
                )) : <StyledTypography>-</StyledTypography>}
              </Box>
            </TwoColumnContainer>
            <TwoColumnContainer label="Alarm out Status">
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {alarmOut.length > 0 ? alarmOut.map((alarm, idx) => (
                  <Box key={idx} sx={{ display: "flex", flexDirection: "column", gap: 0.2 }}>
                    <StyledTypography>
                      Alarm out {alarm.id}:
                    </StyledTypography>
                    <StyledTypography>
                      Valid Signal: {alarm.validSignal}
                    </StyledTypography>
                    <StyledTypography>
                      Alarm out Mode: {alarm.mode}
                    </StyledTypography>
                  </Box>
                )) : <StyledTypography>-</StyledTypography>}
              </Box>
            </TwoColumnContainer>
          </Box>
        </CollapseCard>
      )}
    </Box>
  );
};

export default SurveillancePanel;
